import { useEffect, useState } from "react";
import { AppHeader } from "./components/AppHeader";
import { LogoBand } from "./components/LogoBand";
import { WelcomeBlock } from "./components/WelcomeBlock";
import { InputBar } from "./components/InputBar";
import { ResetConfirmModal } from "./components/ResetConfirmModal";
import { ChatFeed, revealDelay } from "./components/ChatFeed";
import { ReferralForm } from "./components/ReferralForm";
import { LanguageProvider, translate } from "./i18n";
import { HelpModal } from "./components/HelpModal";
import { CoachMessageModal } from "./components/CoachMessageModal";
import { DeclineShareModal } from "./components/DeclineShareModal";
import { serviceName } from "./components/Cards";
import { AccessibilityPanel } from "./components/AccessibilityPanel";
import { ConsentBanner } from "./components/ConsentBanner";
import { track, trackQuestionRetried } from "./analytics/posthog";
import { useSettings } from "./components/useSettings";
import { ProgressBar } from "./components/ProgressBar";
import { useMediaQuery } from "./components/useMediaQuery";
import { ReportProblemModal } from "./components/ReportProblemModal";
import { useConversation } from "./conversation/useConversation";
import { validateInput } from "./conversation/validation";
import type { Option, Turn } from "./api/types";

const optionValue = (o: Option): string =>
  o.value !== undefined && o.value !== null ? String(o.value) : o.text;

function placeholderFor(turn: Turn | null, lang: string): string {
  switch (turn?.data_field) {
    case "postcode":
      return translate(lang, "input.placeholder.postcode");
    case "email":
      return translate(lang, "input.placeholder.email");
    case "phone":
      return translate(lang, "input.placeholder.phone");
    default:
      return translate(lang, "input.typeMessage");
  }
}

const isDob = (t: Turn | null) =>
  t?.data_field === "date_of_birth" || t?.validation?.format === "uk_date_of_birth";

/** Free-text questions have no options; option questions accept typing only when
 *  the engine sets allow_free_text. Crisis/terminal/match-card turns lock input. */
function canType(turn: Turn | null): boolean {
  if (!turn || turn.terminal || turn.crisis) return false;
  if (turn.node_id?.startsWith("CONSENT_PROCEED") || turn.node_id?.startsWith("CONSENT_ALT")) return false;
  const hasOptions = !!turn.options && turn.options.length > 0;
  return !hasOptions || turn.allow_free_text;
}

export function App() {
  const { state, currentTurn, send, retry, reset, lang, languages, languageBusy, changeLanguage } =
    useConversation();
  const settings = useSettings();
  // Phones: the bar hangs off the header instead of the partner strip (Naveen,
  // 23 Sept). Same breakpoint as the strip's own mobile mode in LogoBand.css.
  const barUnderHeader = useMediaQuery("(max-width: 640px)");
  const [referral, setReferral] = useState<{ service: string; proceed: Option } | null>(null);
  // The human-readable referral reference (e.g. "WW-4E5F14"), captured from the
  // submit response and shown on the completion screen once the referral is sent.
  const [referralReference, setReferralReference] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [coachModalOpen, setCoachModalOpen] = useState(false);
  // Reset asks before clearing: it is destructive and irreversible.
  const [resetOpen, setResetOpen] = useState(false);
  // Pending "No, don't share" on the data-sharing consent. Holds the answer we
  // would have sent, so the confirmation pop-up can either discard it ("Go
  // back" — the consent question stays unanswered) or send it ("Continue
  // without sharing"). Declining is never sent until confirmed.
  const [declineShare, setDeclineShare] = useState<
    { message: string; displayText?: string; programme: string } | null
  >(null);
  const [inputError, setInputError] = useState<string | null>(null);
  // Quick-send chips reveal in lock-step with the staged question (so they
  // don't appear during the acknowledgment/typing pause).
  const [chipsReady, setChipsReady] = useState(true);

  useEffect(() => setInputError(null), [state.log.length]);

  const lastBotId = (() => {
    for (let i = state.log.length - 1; i >= 0; i--) {
      if (state.log[i].role === "bot") return state.log[i].id;
    }
    return null;
  })();

  useEffect(() => {
    const staged = !!currentTurn?.acknowledgment && !!currentTurn?.message;
    if (!staged) {
      setChipsReady(true);
      return;
    }
    setChipsReady(false);
    const t = setTimeout(() => setChipsReady(true), revealDelay(currentTurn?.message));
    return () => clearTimeout(t);
    // re-run per new bot turn (lastBotId); currentTurn is read at run time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastBotId]);

  const active = state.status === "active";
  const inputEnabled = active && canType(currentTurn);
  const started = state.log.some((e) => e.role === "user");
  // Quick-send chips: free-text questions that also offer suggested answers.
  const chips =
    active && chipsReady && currentTurn?.allow_free_text && currentTurn.options?.length
      ? currentTurn.options
      : undefined;

  const handleSend = (text: string) => {
    const check = validateInput(text, currentTurn?.validation ?? null);
    if (!check.valid) {
      setInputError(check.error);
      trackQuestionRetried();
      return;
    }
    setInputError(null);
    void send(text, undefined, "typed");
  };

  /**
   * Option clicks from the chat. Everything goes straight through EXCEPT
   * declining the data-sharing consent, which opens a confirmation pop-up
   * first (Yvett 17 Jul) — the decline is only sent once they confirm, so
   * "Go back" leaves the consent question exactly as it was.
   */
  const handleSelect = (message: string, displayText?: string) => {
    // The opening acknowledgement is a "continue", not an answer — it belongs
    // in the flow but not in the transcript as something the resident said.
    if (currentTurn?.node_type === "STATEMENT") {
      void send(message, displayText, "option", { echo: false });
      return;
    }
    const isShareConsent = currentTurn?.node_id?.startsWith("CONSENT_SHARE");
    const isDecline = message === "false";
    if (isShareConsent && isDecline) {
      setDeclineShare({
        message,
        displayText,
        programme: serviceName(currentTurn?.service_recommended),
      });
      return;
    }
    void send(message, displayText);
  };

  const handleReferralSubmit = () => {
    if (!referral) return;
    const { proceed } = referral;
    track("referral_form_submitted", { service: referral.service });
    setReferral(null);
    // Persist the completed referral form to the dual store (best-effort —
    // never block advancing the conversation on it). The form is captured on
    // window._referralForm by ReferralForm on submit.
    const form = (window as unknown as { _referralForm?: Record<string, unknown> })
      ._referralForm;
    if (form && state.sessionId) {
      void import("./api/client")
        .then((api) => api.submitReferral(state.sessionId as string, form))
        .then((res) => setReferralReference(res.reference))
        .catch((e) => console.warn("referral persist failed (non-blocking):", e));
    }
    void send(optionValue(proceed), proceed.text);
  };

  return (
    <LanguageProvider lang={lang}>
    <div className="app-shell" tabIndex={-1}>
      <a className="skip-link" href="#main">
        {translate(lang, "app.skipLink")}
      </a>
      <AppHeader
        onGetHelp={() => {
          // FDD Non-Digital Fallback report: how often the human/urgent routes
          // are used, and (via the last question tracked) at which point.
          track("human_help_clicked", { source: "urgent_help" });
          setHelpOpen(true);
        }}
        onMessageCoach={() => {
          track("human_help_clicked", { source: "coach_message" });
          setCoachModalOpen(true);
        }}
        languages={languages}
        currentLang={lang}
        languageBusy={languageBusy}
        onLanguageChange={(next) => {
          track("language_changed", { lang: next });
          void changeLanguage(next);
        }}
      />
      <main id="main" className="chat-feed" tabIndex={-1} aria-label="Conversation">
        {/* The progress bar hangs off whichever chrome is pinned: under the
            partner strip on desktop (the strip is sticky under the header),
            directly under the header on phones (the strip scrolls away) —
            Naveen, 14 and 23 Sept. Keyed siblings so React MOVES the bar's
            node on a resize across the breakpoint instead of remounting it;
            a remount would reset the bar's high-water mark. */}
        {barUnderHeader
          ? [<ProgressBar key="progress" turn={currentTurn} />, <LogoBand key="strip" />]
          : [<LogoBand key="strip" />, <ProgressBar key="progress" turn={currentTurn} />]}
        <div className="chat-feed-inner">
          <WelcomeBlock />
          {/* Live region so screen readers announce new assistant turns. */}
          <div className="chat-log" role="log" aria-label="Conversation" aria-live="polite" aria-relevant="additions text">
            <ChatFeed
              state={state}
              onSelect={handleSelect}
              onProceed={(service, proceed) => {
                track("referral_form_opened", { service });
                setReferral({ service, proceed });
              }}
              onRetry={() => void retry()}
              referralReference={referralReference}
              onMessageCoach={() => setCoachModalOpen(true)}
            />
          </div>
        </div>
      </main>

      <InputBar
        disabled={!inputEnabled}
        composerHidden={!canType(currentTurn)}
        onReportProblem={() => {
          track("human_help_clicked", { source: "report_problem" });
          setReportOpen(true);
        }}
        placeholder={placeholderFor(currentTurn, lang)}
        mode={isDob(currentTurn) ? "date" : "text"}
        error={inputError}
        onSend={handleSend}
        chips={chips}
        onChip={(message, display) => void send(message, display, "chip")}
        focusOnEnable={started}
        onReset={() => setResetOpen(true)}
      />

      {referral && (
        <ReferralForm
          service={referral.service}
          collectedData={state.collectedData}
          onSubmit={handleReferralSubmit}
          onClose={() => {
            track("referral_form_abandoned", { service: referral.service });
            setReferral(null);
          }}
        />
      )}
      {resetOpen && (
        <ResetConfirmModal
          onCancel={() => setResetOpen(false)}
          onConfirm={() => {
            setResetOpen(false);
            void reset();
          }}
        />
      )}
      <ConsentBanner />
      {declineShare && (
        <DeclineShareModal
          programme={declineShare.programme}
          onGoBack={() => {
            track("share_decline_reconsidered", {});
            setDeclineShare(null);
          }}
          onContinue={() => {
            const { message, displayText } = declineShare;
            setDeclineShare(null);
            track("share_decline_confirmed", {});
            void send(message, displayText);
          }}
          onMessageCoach={() => setCoachModalOpen(true)}
        />
      )}
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
      {reportOpen && (
        <ReportProblemModal
          onClose={() => setReportOpen(false)}
          sessionId={state.sessionId}
        />
      )}
      {coachModalOpen && <CoachMessageModal onClose={() => setCoachModalOpen(false)} />}

      <AccessibilityPanel
        dark={settings.dark}
        setDark={settings.setDark}
        fontScale={settings.fontScale}
        increaseFont={settings.increaseFont}
        decreaseFont={settings.decreaseFont}
        underlineLinks={settings.underlineLinks}
        setUnderlineLinks={settings.setUnderlineLinks}
        reset={settings.reset}
      />
    </div>
    </LanguageProvider>
  );
}
